import { lazy, Suspense, useCallback, useEffect, useMemo } from 'react';
import { displayAddress } from '../domain/inspection';
import { AreaWorkspaceScreen } from '../features/areas/AreaWorkspaceScreen';
import { SignInScreen } from '../features/auth/SignInScreen';
import { DeviceVerifyScreen } from '../features/auth/DeviceVerifyScreen';
import { accountStore, refreshAccount } from '../state/accountStore';
import { flushReportUploads } from '../state/reportCloudStore';
import { flushServerPurges } from '../state/serverPurge';
import { cleanupAbandonedDrafts } from '../state/draftRetention';
import { CloseoutScreen } from '../features/closeout/CloseoutScreen';
import { FindingsScreen } from '../features/findings/FindingsScreen';
import { HomeScreen } from '../features/home/HomeScreen';
import { BulkImportScreen } from '../features/import/BulkImportScreen';
import { ImportChoiceScreen } from '../features/import/ImportChoiceScreen';
import { PhotoPoolScreen } from '../features/import/PhotoPoolScreen';
import { InspectionShell } from '../features/inspection/InspectionShell';
import { InspectionDetailsScreen } from '../features/inspections/InspectionDetailsScreen';
import { MyInspectionsScreen } from '../features/inspections/MyInspectionsScreen';
import { MovedOutScreen } from '../features/inspections/MovedOutScreen';
import { NewInspectionScreen } from '../features/inspections/NewInspectionScreen';
import { PropertyLayoutScreen } from '../features/inspections/PropertyLayoutScreen';
import { ReportScreen } from '../features/report/ReportScreen';
import { ReportsScreen } from '../features/report/ReportsScreen';
import { SettingsPageScreen } from '../features/settings/SettingsPages';
import { SettingsScreen } from '../features/settings/SettingsScreen';
import { AiJobNoticeHost } from '../features/ai/AiJobNoticeHost';
import { AiTaskCenterScreen } from '../features/ai/AiTaskCenterScreen';
import { PushPermissionHost } from '../features/ai/PushPermissionHost';
import { useT } from '../i18n/i18n';
import { useStore } from '../state/createStore';
import { inspectionProgress, inspectionStore } from '../state/inspectionStore';
import { sessionStore } from '../state/sessionStore';
import { headerStatusFor, syncStatusStore } from '../state/syncStatus';
import { BottomTabBar, overlayRoot, ShellTopBar, StatusChip, ToastHost, TopTabNav, type TabItem } from '../ui/components';
import { LaunchOverlay } from './LaunchOverlay';
import { ScreenErrorBoundary } from './ScreenErrorBoundary';
import { useOnlineStatus } from '../ui/hooks';
import { ShellContext, type ShellContextValue } from '../ui/shell';
import { activeTabFor, currentRoute, navStore, resetStack, showsTabBar, switchTab, type Route, type TabId } from './navigation';
import { InspectorAvatar } from '../features/settings/InspectorAvatar';
import { useViewportTier } from './responsive';
import { useScrollRestoration } from './scrollRestore';
import { useFoldScrollAnchor } from './foldScrollAnchor';

// Development-only visual harness; the dynamic import is dead code in production builds.
const FoundationGallery = import.meta.env.DEV ? lazy(() => import('./FoundationGallery').then((m) => ({ default: m.FoundationGallery }))) : null;

// Owner 2026-10-20: an inspection exported to another device is view-only here. Every screen of it — opened
// from a list, a notification, the AI Task Center or Back — shows where it went instead (MovedOutScreen).
function ScreenFor(props: { route: Route }) {
  const route = props.route;
  const inspectionId = 'inspectionId' in route && typeof route.inspectionId === 'string' ? route.inspectionId : null;
  const movedOut = useStore(inspectionStore, (s) => (inspectionId ? (s.inspections.find((i) => i.id === inspectionId)?.movedOut ?? null) !== null : false));
  if (inspectionId && movedOut) return <MovedOutScreen inspectionId={inspectionId} />;
  return <RouteScreen route={route} />;
}

function RouteScreen(props: { route: Route }) {
  const route = props.route;
  switch (route.screen) {
    case 'signIn':
      return <SignInScreen />;
    case 'home':
      return <HomeScreen />;
    case 'inspectionList':
      return <MyInspectionsScreen />;
    case 'newInspection':
      return <NewInspectionScreen />;
    case 'inspection':
      return <InspectionShell inspectionId={route.inspectionId} />;
    case 'propertyLayout':
      return <PropertyLayoutScreen inspectionId={route.inspectionId} />;
    case 'inspectionDetails':
      return <InspectionDetailsScreen inspectionId={route.inspectionId} focus={route.focus} />;
    case 'bulkImport':
      return <BulkImportScreen inspectionId={route.inspectionId} />;
    case 'importChoice':
      return <ImportChoiceScreen inspectionId={route.inspectionId} />;
    case 'photoPool':
      return <PhotoPoolScreen inspectionId={route.inspectionId} focus={route.focus} />;
    case 'areaWorkspace':
      return <AreaWorkspaceScreen inspectionId={route.inspectionId} areaId={route.areaId} itemSheetId={route.itemSheetId} findingId={route.findingId ?? null} advancedJobId={route.advancedJobId ?? null} focusDescriptor={route.focusDescriptor ?? null} />;
    case 'findings':
      return <FindingsScreen inspectionId={route.inspectionId} />;
    case 'closeout':
      return <CloseoutScreen inspectionId={route.inspectionId} />;
    case 'aiTasks':
      return <AiTaskCenterScreen inspectionId={route.inspectionId ?? null} />;
    case 'reports':
      return <ReportsScreen />;
    case 'report':
      return <ReportScreen inspectionId={route.inspectionId} />;
    case 'settings':
      return <SettingsScreen />;
    case 'settingsPage':
      return <SettingsPageScreen page={route.page} />;
    case 'foundationGallery':
      return FoundationGallery ? (
        <Suspense fallback={null}>
          <FoundationGallery />
        </Suspense>
      ) : null;
  }
}

// App shell (task book 2026-09-04 §4 / §19; Final Authority §22–§24):
//   sign-in gate         nothing renders behind the sign-in screen without a session — no guest mode
//   phone + tablet mini  single column · screen-level top bar · bottom 4-tab bar (Home · Inspections ·
//                        Reports · Settings; Home added by the Owner 2026-09-11) on root and
//                        first-level screens only
//   tablet               global top bar (CURRENT PROPERTY · PROGRESS · status · avatar) · at most two
//                        persistent panes · no bottom tab bar
// Both tiers share every screen, store and route; only chrome and layout differ.
// On every sign-in and whenever the app comes back to the front: the account (licence + device, Owner item
// 50), and the queued work of delivered reports (cloud copies and the server-side purge, items 1 / 2 / 25).
function resumeAccountWork(): void {
  void refreshAccount();
  void flushReportUploads();
  void flushServerPurges();
  // Retention rules 2026-10-19: drafts with no final report and no change for 90 days are cleared.
  void cleanupAbandonedDrafts();
}

export function AppShell() {
  const route = useStore(navStore, currentRoute);
  const lastInspectionId = useStore(navStore, (s) => s.lastInspectionId);
  const hydrated = useStore(inspectionStore, (s) => s.hydrated);
  const sessionPhase = useStore(sessionStore, (s) => s.phase);
  const sessionUserId = useStore(sessionStore, (s) => s.userId);
  const accountDevice = useStore(accountStore, (s) => s.device);
  const currentInspection = useStore(inspectionStore, (s) => (lastInspectionId ? (s.inspections.find((i) => i.id === lastInspectionId) ?? null) : null));
  const areas = useStore(inspectionStore, (s) => s.areas);
  const items = useStore(inspectionStore, (s) => s.items);
  const progress = useMemo(
    () => (lastInspectionId ? inspectionProgress(inspectionStore.get(), lastInspectionId) : { total: 0, settled: 0 }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- areas / items are the inputs of inspectionProgress
    [lastInspectionId, areas, items]
  );
  const tier = useViewportTier();
  const { t, locale } = useT();
  const online = useOnlineStatus();
  // A new screen opens at the top; Back returns to where that screen was (src/app/scrollRestore.ts).
  useScrollRestoration();
  // Folding / unfolding keeps the field being typed in, or the block in view, on screen (src/app/foldScrollAnchor.ts).
  useFoldScrollAnchor();
  const sync = useStore(syncStatusStore, (s) => s);
  // Owner 2026-09-14: the avatar IS the inspector's photo, so tapping it lands where the photo is set
  // (Settings › 验屋师资料) with the Settings root one Back away.
  const openAccount = useCallback(() => resetStack([{ screen: 'settings' }, { screen: 'settingsPage', page: 'profile' }]), []);
  const avatar = useMemo(() => <InspectorAvatar />, []);

  const isTablet = tier === 'tablet';
  // The sign-in screen is the only thing a signed-out session can see (development gallery excepted).
  const gated = sessionPhase !== 'signedIn' && !(import.meta.env.DEV && route.screen === 'foundationGallery');
  const effectiveRoute: Route = gated ? { screen: 'signIn' } : route;
  // Owner decision list 2026-10-19 item 50 (L-7): a new device shows only the e-mail code screen until verified.
  const deviceGate = !gated && accountDevice === 'codeRequired';
  const chrome = effectiveRoute.screen !== 'signIn' && !deviceGate;
  const showTabBar = !isTablet && chrome && showsTabBar(effectiveRoute);
  // Round 2 §B: the header shows a status ONLY when it is actionable (offline · pending · failed);
  // the normal online state shows nothing. "Local only" lives under Settings → Sync & Storage.
  const header = headerStatusFor(online, sync);
  const statusChip = header ? <StatusChip state={header.state} count={header.count} testId="shell-status-chip" /> : null;

  const shellValue = useMemo<ShellContextValue>(() => ({ tier, openAccount, statusChip, avatar }), [tier, openAccount, statusChip, avatar]);

  // The account (licence + this device) is asked again on every sign-in and whenever the app comes back
  // to the front — that is how a removed device learns it was removed, and how the licence re-check runs.
  useEffect(() => {
    if (sessionPhase === 'signedIn' && sessionUserId !== null) resumeAccountWork();
  }, [sessionPhase, sessionUserId]);
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && sessionStore.get().phase === 'signedIn' && sessionStore.get().userId !== null) resumeAccountWork();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  // Overlays render outside the app shell (src/ui/components/Portal.tsx): the overlay root carries
  // the chrome facts their CSS needs (tab bar present, viewport tier).
  useEffect(() => {
    const root = overlayRoot();
    root.dataset.tabbar = showTabBar ? 'true' : 'false';
    root.dataset.viewportTier = tier;
  }, [showTabBar, tier]);

  const tabs = useMemo<TabItem<TabId>[]>(
    () => [
      { key: 'home', label: t('nav.home'), icon: 'home' },
      { key: 'inspections', label: t('nav.inspections'), icon: 'apartment' },
      { key: 'reports', label: t('nav.reports'), icon: 'receipt_long' },
      { key: 'settings', label: t('nav.settings'), icon: 'settings' }
    ],
    [t]
  );

  if (!hydrated || sessionPhase === 'unknown') {
    return (
      <div className="app-shell" data-viewport-tier={tier} lang={locale}>
        <div className="boot-screen">{t('shell.loading')}</div>
      </div>
    );
  }

  return (
    <ShellContext.Provider value={shellValue}>
      <div className="app-shell" data-viewport-tier={tier} data-tabbar={showTabBar ? 'true' : 'false'} data-locale={locale} lang={locale}>
        {isTablet && chrome ? (
          <ShellTopBar
            onMenu={openAccount}
            onAvatar={openAccount}
            property={currentInspection ? displayAddress(currentInspection, t('inspection.noAddress')) : null}
            progressValue={progress.settled}
            progressTotal={progress.total}
            status={statusChip}
            avatar={avatar}
            nav={<TopTabNav items={tabs} active={activeTabFor(effectiveRoute)} onSelect={switchTab} />}
          />
        ) : null}
        <div className={`app-frame ${effectiveRoute.screen === 'foundationGallery' ? 'app-frame-bleed' : ''}`}>
          {deviceGate ? (
            <DeviceVerifyScreen />
          ) : (
            <ScreenErrorBoundary resetKey={JSON.stringify(effectiveRoute)}>
              <ScreenFor route={effectiveRoute} />
            </ScreenErrorBoundary>
          )}
        </div>
        {showTabBar ? <BottomTabBar items={tabs} active={activeTabFor(effectiveRoute)} onSelect={switchTab} /> : null}
        <ToastHost />
        {/* Advanced AI (2026-09-13): the foreground completion notice and the one-time notification explainer. */}
        <AiJobNoticeHost />
        <PushPermissionHost />
        <LaunchOverlay />
      </div>
    </ShellContext.Provider>
  );
}
